/*
 * ScheduleCallModal — "Orbit calls you to finish the intake."
 *
 * Opened from the topbar "Pick up later → Schedule a call" path. The user
 * drops their number (and optionally a time); Orbit places an outbound
 * discovery call that resumes their intake exactly where they left off —
 * same dossier, same problem scope. Reuses the consultant-side
 * scheduleVoiceCall API (POST /api/intake/voice/schedule), now self-serve.
 *
 * Brand: light #eef3ff surface, IBM Plex, brand-blue primary, no emoji,
 * sentence case. Compact — one number field + an optional time.
 */

import React, { useState } from 'react';
import { Phone, X, CheckCircle2, CalendarClock } from 'lucide-react';

import { scheduleVoiceCall } from '../../services/api';

export interface ScheduleCallModalProps {
  open: boolean;
  onClose: () => void;
  clientId?: string;
}

export const ScheduleCallModal: React.FC<ScheduleCallModalProps> = ({ open, onClose, clientId }) => {
  const [phone, setPhone] = useState('');
  const [when, setWhen] = useState('');     // datetime-local; empty = as soon as possible
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ at: string | null } | null>(null);

  if (!open) return null;

  const submit = async () => {
    if (!phone.trim()) { setError('Add a number Orbit can call.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const scheduledAt = when ? new Date(when).toISOString() : undefined;
      const res = await scheduleVoiceCall(clientId ?? '', { toNumber: phone.trim(), scheduledAt });
      setDone({ at: res.scheduled_at ?? scheduledAt ?? null });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not schedule the call.');
    } finally {
      setSubmitting(false);
    }
  };

  const prettyWhen = (iso: string | null): string => {
    if (!iso) return 'shortly';
    try {
      return new Date(iso).toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return 'the time you picked'; }
  };

  return (
    <div className="aa-callmodal-backdrop" role="dialog" aria-modal="true" aria-label="Schedule a call with Orbit" onClick={onClose}>
      <div className="aa-callmodal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="aa-callmodal-close" onClick={onClose} aria-label="Close">
          <X size={16} strokeWidth={2.25} />
        </button>

        {done ? (
          <div className="aa-callmodal-done">
            <span className="aa-callmodal-done-icon"><CheckCircle2 size={22} strokeWidth={2.25} /></span>
            <h3 className="aa-callmodal-title">You're set.</h3>
            <p className="aa-callmodal-sub">
              Orbit will call you <strong>{prettyWhen(done.at)}</strong> on{' '}
              <strong>{phone.trim()}</strong> and pick up the intake exactly where you left off —
              same problem, same dossier. Nothing to repeat.
            </p>
            <button type="button" className="aa-callmodal-primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <span className="aa-callmodal-eyebrow"><CalendarClock size={13} strokeWidth={2.25} /> Schedule a call</span>
            <h3 className="aa-callmodal-title">Let Orbit call you to finish.</h3>
            <p className="aa-callmodal-sub">
              Drop your number and Orbit will phone you to continue scoping —
              resuming right where you left off. Leave the time empty and it'll call as soon as it can.
            </p>

            <label className="aa-callmodal-label" htmlFor="aa-call-phone">Your number</label>
            <div className="aa-callmodal-input-wrap">
              <Phone size={14} strokeWidth={2} className="aa-callmodal-input-icon" />
              <input
                id="aa-call-phone"
                className="aa-callmodal-input"
                type="tel"
                inputMode="tel"
                placeholder="+1 555 123 4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoFocus
              />
            </div>

            <label className="aa-callmodal-label" htmlFor="aa-call-when">
              When <span className="aa-callmodal-label-hint">— optional, empty = as soon as possible</span>
            </label>
            <input
              id="aa-call-when"
              className="aa-callmodal-input aa-callmodal-input--when"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />

            {error && <p className="aa-callmodal-error">{error}</p>}

            <button
              type="button"
              className="aa-callmodal-primary"
              onClick={submit}
              disabled={submitting}
            >
              {submitting ? 'Scheduling…' : 'Schedule the call'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ScheduleCallModal;
